import React, { useState } from "react";
import { Box, Skeleton } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";
import { commaNotation, formatPercent } from "@utils";
import typography from "src/theme/typography";

const TOOLTIP_BOX_WIDTH = 240;
const TOOLTIP_BOX_HEIGHT = 110;
const TOOLTIP_BAR_GAP = 14;

const formatTick = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(value);
};

const niceAxisMax = (value) => {
  if (value <= 0) return 4;
  const targetInterval = (value * 1.15) / 4;
  const power = Math.pow(10, Math.floor(Math.log10(targetInterval)));
  const ratio = targetInterval / power;
  let nice;
  if (ratio <= 1) nice = 1;
  else if (ratio <= 2) nice = 2;
  else if (ratio <= 4) nice = 4;
  else if (ratio <= 5) nice = 5;
  else nice = 10;
  return 4 * nice * power;
};

const HyroBarChart = ({ data = [], height = 400, loading = false, showTrack = true, padding = 0.5, total: totalProp, renderTooltip: renderTooltipProp }) => {
  const theme = useTheme();
  const [hoveredKey, setHoveredKey] = useState(null);

  if (loading) {
    return (
      <Box sx={{ height, width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const total = totalProp ?? data[0]?.value ?? 0;
  const chartData = data.map((d) => ({ ...d, total }));
  const dataMax = Math.max(0, ...data.map((d) => d.value || 0));
  const yMax = niceAxisMax(dataMax);
  const yTickValues = Array.from({ length: 5 }, (_, i) => (yMax / 4) * i);

  const defaultRenderTooltip = (datum) => (
    <BarTooltip>
      <strong>{datum.label}</strong>
      <span>{commaNotation(datum.value)} ({formatPercent(datum.value, datum.total)})</span>
    </BarTooltip>
  );

  const renderTooltip = (label, value, totalRef, datum) =>
    (renderTooltipProp ?? defaultRenderTooltip)({ ...(datum ?? {}), label, value, total: totalRef });

  const TrackLayer = ({ bars, yScale }) => {
    const trackTop = yScale(total);
    const trackBottom = yScale(0);
    const trackHeight = Math.max(0, trackBottom - trackTop);
    return (
      <g>
        {bars.map((bar) => (
          <rect
            key={`track-${bar.data.indexValue}`}
            x={bar.x}
            y={trackTop}
            width={bar.width}
            height={trackHeight}
            rx={4}
            fill={theme.palette.info[200]}
            style={{ pointerEvents: "none" }}
          />
        ))}
      </g>
    );
  };

  const InteractionLayer = ({ bars, yScale }) => {
    const trackTop = yScale(total);
    const trackBottom = yScale(0);
    const trackHeight = Math.max(0, trackBottom - trackTop);
    const hoveredBar = bars.find((b) => b.data.indexValue === hoveredKey);
    return (
      <g>
        {bars.map((bar) => (
          <rect
            key={`hover-${bar.data.indexValue}`}
            x={bar.x}
            y={trackTop}
            width={bar.width}
            height={trackHeight}
            fill="transparent"
            style={{ pointerEvents: "all" }}
            onMouseMove={() => {
              if (hoveredKey !== bar.data.indexValue) setHoveredKey(bar.data.indexValue);
            }}
            onMouseLeave={() => setHoveredKey(null)}
          />
        ))}
        {hoveredBar && (
          <foreignObject
            x={hoveredBar.x + (hoveredBar.width / 2) - (TOOLTIP_BOX_WIDTH / 2)}
            y={(hoveredBar.data.value > 0 ? hoveredBar.y : trackTop) - TOOLTIP_BOX_HEIGHT - TOOLTIP_BAR_GAP}
            width={TOOLTIP_BOX_WIDTH}
            height={TOOLTIP_BOX_HEIGHT}
            style={{ overflow: "visible", pointerEvents: "none" }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "flex-end", height: "100%" }}>
              {renderTooltip(
                hoveredBar.data.indexValue,
                hoveredBar.data.value,
                hoveredBar.data.data?.total ?? total,
                hoveredBar.data.data
              )}
            </Box>
          </foreignObject>
        )}
      </g>
    );
  };

  const layers = ["grid", "axes"];
  if (showTrack) layers.push(TrackLayer);
  layers.push("bars", "markers", "legends", InteractionLayer);

  return (
    <Box sx={{ height, width: "100%" }}>
      <ResponsiveBar
        data={chartData}
        keys={["value"]}
        indexBy="label"
        margin={{ top: 16, right: 0, bottom: 40, left: 40 }}
        padding={padding}
        valueScale={{ type: "linear", min: 0, max: yMax }}
        indexScale={{ type: "band", round: true }}
        colors={[theme.palette.secondary[500]]}
        borderRadius={4}
        enableLabel
        labelSkipHeight={20}
        labelTextColor={theme.palette.system[0]}
        labelPosition="end"
        labelOffset={-22}
        label={(d) => `${commaNotation(d.value)} (${formatPercent(d.value, d.data?.data?.total ?? total)})`}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 16,
          renderTick: ({ x, y, value }) => (
            <g transform={`translate(${x},${y + 16})`}>
              <text
                textAnchor="middle"
                dominantBaseline="hanging"
                style={{ ...typography.subtitle2, fill: theme.palette.text[800] }}
              >
                {value}
              </text>
            </g>
          )
        }}
        axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: yTickValues, format: formatTick }}
        enableGridY
        gridYValues={yTickValues}
        enableGridX={false}
        layers={layers}
        isInteractive={false}
        theme={{
          axis: {
            ticks: { text: { fill: theme.palette.text[700], fontSize: 10, fontFamily: "inter,sans-serif" } }
          },
          grid: { line: { stroke: theme.palette.system[200], strokeDasharray: "0" } },
          labels: { text: { fontSize: 14, fontFamily: "inter,sans-serif", letterSpacing: "0.1px" } }
        }}
        animate
        motionConfig="gentle"
      />
    </Box>
  );
};

const BarTooltip = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(0.75),
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: theme.shadows[2],
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  ...theme.typography.caption,
  minWidth: 120,
  color: theme.palette.text[800]
}));

export default HyroBarChart;
